import { useMemo } from 'react';
import { useGetProjectRespondentLookup } from '@/reactQuery/get';
import type { ResearchArtifact } from '@/reactQuery/researchAssistant';

/**
 * Shared respondent → conversation lookup type. Indexed by
 * `respondent_index` (the number a researcher sees in `[R12]`),
 * mapped to the conversation_id needed to open the transcript panel.
 *
 * Lives in its own file (not co-located with `CitationPillText`) so
 * the React-refresh linter doesn't complain about the component file
 * also exporting a hook + type. Fast Refresh wants components-only
 * exports per file.
 */
export type RespondentMap = Map<
  number,
  { conversationId: string; messageId?: string; questionId?: string }
>;

type ArtifactQuote = {
  respondent_index?: number | string;
  conversation_id?: string;
  message_id?: string;
  question_id?: string;
};

/**
 * TELV1-427 — per-citation scroll target attached to `markdown_report` /
 * `theme_summary` artifacts (`data.citations`) by the analyzer's
 * `attachReportCitations`. Same scroll fields as a quote, keyed by the
 * respondent index the report cites via `[Rn]`.
 */
type ArtifactCitation = {
  respondent_index?: number | string;
  conversation_id?: string;
  message_id?: string;
  question_id?: string;
};

/**
 * Build a respondent → conversation lookup for the Research Assistant
 * citation pills (`[R12]` etc.).
 *
 * Two data sources, layered:
 *
 *   1. **Project-level lookup** (base layer) — `useGetProjectRespondentLookup`
 *      returns every (respondent_index → conversation_id) pair for the
 *      project. Fetched once and cached, so every respondent that
 *      exists in the project is clickable, regardless of whether any
 *      artifact in the current thread happens to mention them. Without
 *      this, theme_summary / markdown_report artifacts that cite
 *      respondents who aren't in the thread's quote_collection
 *      rendered disabled grey pills — which was the original UX bug.
 *
 *   2. **Artifact-scanned data** (overlay layer) — when a
 *      quote_collection artifact in the thread carries richer
 *      metadata for a respondent (specifically `message_id` and
 *      `question_id`), we prefer that entry so the transcript panel
 *      can scroll to the specific message instead of just opening at
 *      the top.
 *
 * Pass `projectId` to enable the project lookup. Pass `artifacts` to
 * enable the overlay. Either can be omitted (e.g. tests, isolated
 * embeds) and the hook degrades gracefully.
 */
export function useRespondentMap(
  artifacts: ResearchArtifact[] | undefined,
  projectId?: string,
): RespondentMap {
  const { data: projectLookup } = useGetProjectRespondentLookup(projectId);

  return useMemo(() => {
    const map: RespondentMap = new Map();

    // Base layer — project-wide respondent → conversation pairs.
    (projectLookup ?? []).forEach((entry) => {
      if (typeof entry.respondent_index === 'number' && typeof entry.conversation_id === 'string') {
        map.set(entry.respondent_index, {
          conversationId: entry.conversation_id,
        });
      }
    });

    // Overlay layer — artifact metadata may carry richer scroll-target info
    // (message_id / question_id) so the transcript panel scrolls to the cited
    // message instead of opening at the top.
    //
    // Merge semantics: FIRST overlay writer wins for the scroll target, and the
    // base conversationId is preserved when an overlay entry lacks one. So we
    // apply the precise per-quote `quote_collection` message_ids first, then
    // (TELV1-427) the respondent-level `markdown_report` / `theme_summary`
    // citations fill in any respondent the quote collection didn't cover —
    // without clobbering a quote's exact message_id.
    const applyOverlay = (
      idx: number,
      ref: { conversationId?: string; messageId?: string; questionId?: string },
    ) => {
      if (Number.isNaN(idx)) return;
      const existing = map.get(idx);
      const conversationId = ref.conversationId || existing?.conversationId;
      // No conversation to open → don't create a broken clickable pill.
      if (!conversationId) return;
      map.set(idx, {
        conversationId,
        messageId: existing?.messageId ?? ref.messageId,
        questionId: existing?.questionId ?? ref.questionId,
      });
    };

    (artifacts ?? [])
      .filter((artifact) => artifact.type === 'quote_collection')
      .forEach((artifact) => {
        const quotes: ArtifactQuote[] = artifact.data?.quotes || [];
        quotes.forEach((q) => {
          if (q.respondent_index == null || !q.conversation_id) return;
          applyOverlay(Number(q.respondent_index), {
            conversationId: q.conversation_id,
            messageId: q.message_id,
            questionId: q.question_id,
          });
        });
      });

    // TELV1-427 — report/theme `[Rn]` citations. Respondent-level scroll
    // targets harvested server-side; fills respondents not in a quote_collection.
    (artifacts ?? [])
      .filter(
        (artifact) => artifact.type === 'markdown_report' || artifact.type === 'theme_summary',
      )
      .forEach((artifact) => {
        const citations: ArtifactCitation[] = artifact.data?.citations || [];
        citations.forEach((c) => {
          if (c.respondent_index == null) return;
          applyOverlay(Number(c.respondent_index), {
            conversationId: c.conversation_id,
            messageId: c.message_id,
            questionId: c.question_id,
          });
        });
      });

    return map;
  }, [artifacts, projectLookup]);
}
